import {
  Box,
  Button,
  Cards,
  Grid,
  Input,
  Link,
  Pagination,
  Select,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-core';
import {
  ContentSubType,
  ContentType,
  Publication,
  UserType,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useEffect, useState, useContext } from 'react';
import { MplServiceClientFactory } from '../../../api/MplServiceClientFactory';
import { contentSubTypeDisplayNames } from '../../utils/DisplayNames';
import arraySort from 'array-sort';
import ReleaseNoteList from './ReleaseNoteList';
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  getSelectOption,
  getFilterCounterText,
  tablePaginationAriaLabels,
} from '../../../api/UtilsApi';
import { TableEmptyState, TableHeader, TableNoMatchState } from '../../common/CommonComponents';
import { AppContext } from '../../../contexts/AppContext';
import { isAllowedToEditPost, userTypeToAudienceMap } from '../../../api/ContentUtil';
import BrowserHistory from '../../utils/BrowserHistory';
import RouterPaths from '../../../common/RouterPaths';
import { getHighlightedHTML, getHighlightedText } from '../../utils/SearchResultUtils';
import { PostTypeOptions } from '../../utils/SelectableOptions';
import constants from '../../../common/constants';
import { TiptapEditorView } from '@amzn/awscat-editor';
import ReactRouterProps from '../../utils/ReactRouterProps';
import { APGLEntityEntry } from '@amzn/awscat-editor/dist/extensions/apgl-entity';
import { loadEntities } from '../../utils/EntitiesUtils';
import ErrorBoundary from '../../../editor/ErrorBoundary';

const CARD_PAGE_SIZE = 10;

const mplService = MplServiceClientFactory.create();
const defaultPostTypeFilter = getSelectOption(constants.WHATS_NEW_CARD_DEFAULT_POST_TYPES);

const WhatsNew = (props: ReactRouterProps) => {
  const filterPostTypeOptions = [defaultPostTypeFilter, ...PostTypeOptions];
  const [allPosts, setAllPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPostType, setSelectedPostType] = useState(defaultPostTypeFilter);
  const [filteringText, setFilteringText] = useState('');

  const [entities, setEntities] = useState<{ [key: string]: APGLEntityEntry }>();

  const appContext = useContext(AppContext);

  useEffect(() => {
    const fetchPosts = async () => {
      // @ts-expect-error TS2339 - strict migration
      const { userProfile } = appContext.authnInfo;
      setIsLoading(true);
      try {
        const { publications } = await mplService.listPublicationsThroughS3();
        const posts = publications
          .filter((p) => p.contentMetadata.contentType === ContentType.Post)
          .filter(
            (p) =>
              isAllowedToEditPost(userProfile) ||
              // @ts-expect-error TS2532 - strict migration
              userTypeToAudienceMap.get(userProfile.type as UserType).includes(p.contentAudience),
          );
        // @ts-expect-error TS2345 - strict migration
        setAllPosts(posts);
        const entityPayload = await loadEntities();
        setEntities(entityPayload);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, [appContext.authnInfo]);

  useEffect(() => {
    const postType = props?.location?.state?.postType;
    if (postType === constants.WHATS_NEW_CARD_DEFAULT_POST_TYPES || !postType) {
      setSelectedPostType(defaultPostTypeFilter);
    } else {
      setSelectedPostType(
        getSelectOption(postType, contentSubTypeDisplayNames.get(postType as ContentSubType)),
      );
    }
  }, [props.location.state]);

  const highlightMatchFilter = (text: string) => {
    return text ? getHighlightedText(text, filteringText) : '';
  };

  const filterPosts = (post: Publication, filterText: string): boolean => {
    const typeFilter = (item: Publication) =>
      selectedPostType.value === defaultPostTypeFilter.value ||
      selectedPostType.value?.includes(item.contentTypeMetadata.contentSubType);

    const metadataFilter = (item: Publication) =>
      item?.contentMetadata?.contentName?.toLocaleLowerCase()?.includes(filteringTextLowerCase) ||
      item?.contentTypeMetadata?.contentSubType
        ?.toLocaleLowerCase()
        ?.includes(filteringTextLowerCase) ||
      item?.templateMetadata?.summaryPreview?.toLocaleLowerCase()?.includes(filteringTextLowerCase);

    const filteringTextLowerCase = filteringText.toLocaleLowerCase().trim();

    return typeFilter(post) && metadataFilter(post);
  };

  arraySort(allPosts, 'lastUpdated', { reverse: true });
  const releaseNotes = allPosts.filter(
    // @ts-expect-error TS2339 - strict migration
    (p) => p.contentTypeMetadata.contentSubType === ContentSubType.ReleaseNotes,
  );

  const updateFilterInput = (filterText: string) => {
    setFilteringText(filterText);
    actions.setFiltering(filterText);
  };

  const clearFilters = () => {
    updateFilterInput('');
    setSelectedPostType(defaultPostTypeFilter);
  };

  const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } =
    useCollection(allPosts, {
      filtering: {
        empty: <TableEmptyState />,
        noMatch: <TableNoMatchState onClearFilter={clearFilters} />,
        filteringFunction: filterPosts,
      },
      pagination: { pageSize: CARD_PAGE_SIZE },
    });

  const inputFilter = (
    <div className="input-filter">
      <Input
        type="search"
        value={filterProps.filteringText}
        onChange={({ detail }) => updateFilterInput(detail.value)}
        ariaLabel="Filter posts"
        ariaRequired={false}
        placeholder="Search posts"
        // @ts-expect-error TS2322 - strict migration
        ariaDescribedby={null}
      />
    </div>
  );

  const postTypeFilter = (
    <Select
      className="select-filter"
      options={filterPostTypeOptions}
      selectedOption={selectedPostType}
      onChange={({ detail }) => setSelectedPostType(detail.selectedOption as any)}
      ariaRequired={false}
      // @ts-expect-error TS2322 - strict migration
      ariaDescribedby={null}
    />
  );

  const filterCountText = (filterProps.filteringText ||
    selectedPostType.value !== defaultPostTypeFilter.value) && (
    // @ts-expect-error TS2345 - strict migration
    <span className="filtering-results">{getFilterCounterText(filteredItemsCount)}</span>
  );

  const postList = (
    <Cards
      {...collectionProps}
      cardDefinition={{
        header: (item: Publication) => {
          const contentSubType = item?.contentTypeMetadata?.contentSubType;
          return (
            <Box display="inline-block" color="text-body-secondary">
              <Box variant="strong">Posted</Box> {item.lastUpdated.toISODate()}{' '}
              <span>&middot;</span>{' '}
              <Box variant="strong">
                <a
                  onClick={() =>
                    setSelectedPostType(
                      getSelectOption(
                        contentSubType,
                        contentSubTypeDisplayNames.get(contentSubType),
                      ),
                    )
                  }
                >
                  {/* @ts-expect-error TS2345 - strict migration */}
                  {highlightMatchFilter(contentSubTypeDisplayNames.get(contentSubType))}
                </a>
              </Box>
            </Box>
          );
        },
        sections: [
          {
            id: 'title',
            content: (item: Publication) => (
              <SpaceBetween size="m">
                <Link
                  fontSize="heading-xl"
                  variant="secondary"
                  href={`/post-viewer/${item.contentId}`}
                >
                  {highlightMatchFilter(item.contentMetadata.contentName)}
                </Link>
                <ErrorBoundary
                  fallback={
                    <TiptapEditorView
                      model={getHighlightedHTML(
                        item.templateMetadata?.summaryPreview,
                        filteringText,
                      )}
                      // @ts-expect-error TS2322 - strict migration
                      entities={entities}
                    />
                  }
                >
                  <TiptapEditorView
                    model={getHighlightedHTML(item.templateMetadata?.summaryPreview, filteringText)}
                    // @ts-expect-error TS2322 - strict migration
                    entities={entities}
                  />
                </ErrorBoundary>
                <Link variant="secondary" href={`/post-viewer/${item.contentId}`}>
                  Read more
                </Link>
              </SpaceBetween>
            ),
          },
        ],
      }}
      cardsPerRow={[{ cards: 1 }]}
      items={items}
      loadingText="Loading posts"
      pagination={
        <Pagination
          {...paginationProps}
          ariaLabels={tablePaginationAriaLabels}
          disabled={isLoading}
        />
      }
      filter={
        <div className="filter-container">
          {inputFilter}
          {postTypeFilter}
          {filterCountText}
        </div>
      }
      header={
        <TableHeader
          title={'All Posts'}
          actionButtons={
            // @ts-expect-error TS2345 - strict migration
            isAllowedToEditPost(appContext.authnInfo?.userProfile) && (
              <Button onClick={() => BrowserHistory.push(`${RouterPaths.CREATE_POST}`)}>
                Create Post
              </Button>
            )
          }
        />
      }
    />
  );

  return isLoading ? (
    <Spinner />
  ) : (
    <SpaceBetween direction="vertical" size="s">
      <Box variant="h2">What's New</Box>
      <hr />
      <Grid className="create-post-form" gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
        <div className="search-result-list">{postList}</div>
        <ReleaseNoteList isLoading={isLoading} releaseNotes={releaseNotes} />
      </Grid>
    </SpaceBetween>
  );
};

export default WhatsNew;
